// ============================================================================
//  MultiVetTemplate.js — apply one condition template to a whole selection
// ----------------------------------------------------------------------------
//  "We want to automatically vet multiple claims with the template."
//
//  Multi Vet's other five acts write through /claims/multivet/batch, whose answer is a
//  tally for a basket. This one writes through /claims/template-apply, whose answer is a
//  VERDICT PER CLAIM — because a template carries an age band, a sex and a setting, and
//  a group of 300 claims will hold some it suits and some it does not.
//
//  That difference is why this brings its own card and its own Apply rather than being
//  bent through mvAgg / mvMergePass / mvSettle. Teaching those a second response shape
//  would have made the shared path answer two questions badly.
//
//  ── WHAT IT KEEPS FROM THE SHARED PATH, DELIBERATELY ────────────────────────
//  Four rules, restated here because they are the ones that make a bulk edit safe:
//
//    1. DRY RUN FIRST. Nothing is written until the server has said, per claim, what
//       would happen. Apply is dead until that answer is in hand.
//    2. CHUNKED AT MV_RUN_CHUNK, sequentially. The endpoint refuses more than 200 in a
//       request and that limit is right; 200 is a limit on a REQUEST, not on the day's
//       work, so the screen splits it and reports the total. Never parallel — ten
//       concurrent passes would hold ten transactions against the same month.
//    3. THE DAMAGE IS NAMED BEFORE IT HAPPENS. 'Replace' deletes rows. The confirmation
//       says how many and on how many claims, and defaults to Cancel.
//    4. A PASS THAT FAILS LEAVES THE EARLIER ONES WRITTEN, and the report says so.
//       Saying nothing about rows that are already saved is the one report worse than a
//       failure.
//
//  ── A CLAIM IT DOES NOT SUIT IS SKIPPED, NOT A REASON TO STOP ───────────────
//  The other acts refuse the whole run when the cohort's age band is mixed. That is
//  right for a hand-built basket, where the officer chose every code. It is wrong here:
//  an officer applying a malaria template to 300 claims needs to know WHICH eleven
//  cannot take it, not that the run is off. So refusals are listed by patient and the
//  rest go ahead.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Layers, Search, Check, Ban, AlertTriangle, Loader2, Trash2, Plus, Users,
  Stethoscope,
} from 'lucide-react';

import { conditionTemplates, templateApplyCheck, templateApply } from '../BaseURL';
//  The same tokens ClaimsMenu uses. Imported rather than taken as a prop: it is a
//  module-level constant, and a prop would let this screen be handed a different palette
//  from the dialog it sits inside — which is how one panel comes to look foreign.
import { BRAND } from '../../brand';

//  Kept in step with MV_RUN_CHUNK in ClaimsMenu.js and MAX_CLAIMS on both routes. Three
//  numbers that must agree; the screen splitting at a size the server refuses would fail
//  on the first pass of every large run.
const CHUNK = 200;

const chunk = (list, size = CHUNK) => {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

const MODES = [
  { key: 'replace', label: 'Replace what is there',
    hint: 'Removes each claim\'s current entries and writes the template\'s instead.' },
  { key: 'add', label: 'Add to what is there',
    hint: 'Keeps what each claim carries and adds anything the template has that it does not.' },
];

//  ── AGGREGATING PASSES ──────────────────────────────────────────────────────
//  One question asked of 1,894 claims in ten requests, answered as one. Claims are
//  concatenated rather than counted, because the refusals have to be readable per
//  patient — that is the whole output of this screen.
const settle = (parts) => {
  const claims = parts.flatMap((p) => p.claims || []);
  const t = (k) => parts.reduce((n, p) => n + (p.tally?.[k] || 0), 0);
  return {
    template: parts[0]?.template || null,
    mode: parts[0]?.mode || 'replace',
    claims,
    tally: {
      total: claims.length,
      ok: t('ok'), refused: t('refused'), missing: t('missing'),
      removes: t('removes'), adds: t('adds'),
    },
  };
};

export default function MultiVetTemplate({
  //  The ticked claims, as the parent already resolved them. This component never
  //  decides who is in the run.
  claimIds,
  //  Called after a real write so the parent can re-read the month: applying a template
  //  changes what claims are billed on, and the table behind this shows those codes.
  onApplied,
}) {
  const [list, setList] = useState(null);
  const [listErr, setListErr] = useState(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const [mode, setMode] = useState('replace');
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(null);
  const [err, setErr] = useState(null);

  const ids = useMemo(() => (claimIds || []).map(String), [claimIds]);
  const idKey = ids.join(',');

  useEffect(() => {
    let alive = true;
    axios.get(conditionTemplates)
      .then(({ data }) => { if (alive) setList(data?.templates || []); })
      .catch((e) => {
        if (!alive) return;
        setList([]);
        setListErr(e?.response?.data?.error || e.message);
      });
    return () => { alive = false; };
  }, []);

  // ── WHAT THIS SELECTION ACTUALLY IS ───────────────────────────────────────
  //
  //  "The out-patient conditions — make it work only for OPD cases… and the in-patient,
  //  only present in-patient templates. Every claim that is multi-vetted will have to be
  //  an in-patient claim."
  //
  //  So the library is narrowed by what was TICKED, and the shape is read from the
  //  claims rather than from a filter the officer set — the same reasoning Multi Vet's
  //  age guard already uses: "the only trustworthy answer to 'are these children?' is the
  //  claims' own dates of birth."
  //
  //  It comes off the SELECTION endpoint's own summary, which the parent already has, so
  //  no extra call. Until a template is picked there is no /check to ask, and this is the
  //  narrowing the officer needs BEFORE picking.
  const [cohort, setCohort] = useState(null);
  useEffect(() => {
    if (!ids.length) { setCohort(null); return undefined; }
    let alive = true;
    axios.post(templateApplyCheck, { claimIds: ids.slice(0, CHUNK), cohortOnly: true })
      .then(({ data }) => { if (alive) setCohort(data?.cohort || null); })
      .catch(() => { if (alive) setCohort(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey]);

  //  A template's own words for why it cannot suit this selection, decided BEFORE it is
  //  picked. Null means it may be tried.
  const templateOff = useCallback((t) => {
    if (!cohort) return null;
    const set = t.setting || 'outpatient';
    //  A MIXED selection narrows nothing — every template is wrong for part of it, and
    //  saying so on each row would be noise. The banner above says to split it.
    if (cohort.settings && cohort.settings.length > 1) return null;
    const only = cohort.settings && cohort.settings[0];
    if (only && set !== only) {
      return only === 'inpatient'
        ? 'These are admissions. An out-patient template cannot go on them.'
        : 'These are out-patient claims. An admission template cannot go on them.';
    }
    //  ── AND THE DAYS MUST MATCH ─────────────────────────────────────────────
    //  "You have done a condition for four days — you cannot select two days of
    //  in-patient clients to apply to that of the four days."
    //
    //  MATCHED AGAINST EVERY LENGTH IN THE COHORT, not only against a cohort of one
    //  length. This used to give up the moment a selection held two lengths, so a
    //  4-day template stayed on offer for a mixed 2-and-3-day group — the one case
    //  where the officer most needs the list narrowed. A template is off when it
    //  matches NONE of the lengths present; if it matches one of several, it stays and
    //  the per-claim check names the rest.
    const lens = (cohort.stayDays || []).filter((n) => Number.isFinite(Number(n)));
    if (set === 'inpatient' && t.stayDays && lens.length && !lens.includes(t.stayDays)) {
      const said = lens.length === 1
        ? `${lens[0]} day${lens[0] === 1 ? '' : 's'}`
        : `${lens.slice(0, -1).join(', ')} and ${lens[lens.length - 1]} days`;
      return `This is a ${t.stayDays}-day template and these claims stayed ${said}.`;
    }
    return null;
  }, [cohort]);

  //  ── ONLY WHAT CAN ACTUALLY BE USED ────────────────────────────────────────
  //  "If it is an in-patient, it will just show in-patient alone… and if it is
  //  out-patient, it doesn't need to show the in-patient at all at all at all."
  //
  //  These used to be listed greyed-out with their reason, on the reasoning that a
  //  template the officer knows they built and cannot find reads as data loss. That
  //  was wrong for the common case: on a 2-day admission run the list was mostly rows
  //  that cannot be clicked, and the one usable template had to be picked out of them.
  //
  //  So they are HIDDEN, and the count is not. The line under the list says how many
  //  and opens them on request — which answers "where did my template go?" without
  //  making every officer scroll past the answer every time.
  const { usable, closed } = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = list || [];
    const hit = !needle ? all : all.filter((t) => [
      t.name, ...(t.conditions || []).map((c) => `${c.icd10_code} ${c.name}`),
    ].join(' ').toLowerCase().includes(needle));
    return {
      usable: hit.filter((t) => !templateOff(t)),
      closed: hit.filter((t) => templateOff(t)),
    };
  }, [list, q, templateOff]);

  //  Off by default, and reset whenever the selection changes: "show me the ones that
  //  do not fit" is a question about THIS group, and carrying the answer to the next
  //  one puts the noise back.
  const [showClosed, setShowClosed] = useState(false);
  useEffect(() => { setShowClosed(false); }, [idKey]);

  const shown = useMemo(
    () => (showClosed ? [...usable, ...closed] : usable),
    [usable, closed, showClosed]
  );

  //  ── THE DRY RUN ───────────────────────────────────────────────────────────
  //  Re-run whenever the template, the mode OR THE SELECTION changes. The last of
  //  those is the one easy to forget: a plan for 42 claims sitting above an Apply
  //  aimed at 300 is the worst version of this screen.
  const checkAll = useCallback(async () => {
    if (!picked || !ids.length) { setPlan(null); return; }
    setBusy(true);
    setErr(null);
    setPlan(null);
    const passes = chunk(ids);
    const parts = [];
    try {
      for (let i = 0; i < passes.length; i += 1) {
        if (passes.length > 1) setPhase({ what: 'Checking', pass: i + 1, of: passes.length });
        // eslint-disable-next-line no-await-in-loop
        const { data } = await axios.post(templateApplyCheck, {
          templateId: picked, claimIds: passes[i], mode,
        });
        parts.push(data);
      }
      setPlan(settle(parts));
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
      setPhase(null);
    }
  }, [picked, mode, ids]);

  useEffect(() => {
    const t = setTimeout(() => { checkAll(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, mode, idKey]);

  const refusedClaims = useMemo(
    () => (plan?.claims || []).filter((c) => c.verdict === 'refused'), [plan]
  );

  //  ── WHAT THESE CLAIMS COME OUT BILLED ON ──────────────────────────────────
  //  On an out-patient claim the SERVICE is the principal G-DRG, and it lives on a
  //  diagnosis row — so a 'replace' can take it away and leave the claim billed on a
  //  diagnosis code instead. That happened, silently, and the specialty moved with it.
  //  The server now carries the service forward and reports both ends; this counts them
  //  so the officer sees it before authorising the run rather than in an NHIA rejection.
  const billed = useMemo(() => {
    const ok = (plan?.claims || []).filter((c) => c.verdict === 'ok' && c.plan?.billedOn);
    return {
      carried: ok.filter((c) => c.plan.billedOn.carried).length,
      noService: ok.filter((c) => c.plan.billedOn.noService).length,
      //  A change the officer did NOT ask for is the one worth naming: the template
      //  named a service and it differs from what the claim is billed on today.
      changed: ok.filter((c) => {
        const b = c.plan.billedOn;
        return b.before && b.after && b.before.code !== b.after.code;
      }).length,
    };
  }, [plan]);

  //  Refusals GROUPED BY REASON, not listed per claim. Eleven claims refused for the
  //  same reason is one sentence and a count; eleven sentences is a wall an officer
  //  scrolls past. The patients are named underneath, because "11 claims" is not
  //  something anyone can check their own work against.
  const refusalGroups = useMemo(() => {
    const by = new Map();
    for (const c of refusedClaims) {
      for (const r of (c.refusals || [])) {
        //  Keyed on the KIND and the code rather than on the sentence: the sentence
        //  names the patient, so keying on it would give one group per claim and group
        //  nothing at all.
        //  `reason` as well as the kind: an age refusal is EITHER "this tariff is
        //  for the other band" or "this template is for another age range", and
        //  they are different mistakes with different fixes. Grouping them together
        //  is what once let a range refusal be reported as a child tariff.
        const key = `${r.kind}:${r.reason || ''}:${r.code || ''}`;
        if (!by.has(key)) {
          by.set(key, {
            kind: r.kind, reason: r.reason || null,
            code: r.code || null, name: r.name || null, who: [],
            //  THE SENTENCE COMES FROM THE SERVER. It is the only side that knows
            //  which rule fired, and a client that re-derives it from the kind
            //  alone will eventually describe the wrong rule — it did: 17 claims
            //  refused for being outside a 19–45 template were reported as
            //  children on a child tariff, and the officer went looking for a
            //  broken date of birth.
            whyAll: r.whyAll || null,
          });
        }
        by.get(key).who.push(c);
      }
    }
    return [...by.values()].sort((a, b) => b.who.length - a.who.length);
  }, [refusedClaims]);

  const t = plan?.tally;
  const ready = !!plan && !busy && (t?.ok || 0) > 0;

  const apply = async () => {
    if (!ready) return;
    const chosen = (list || []).find((x) => x.id === picked);
    const passes = chunk(ids.filter((id) => {
      const c = (plan.claims || []).find((x) => String(x.id) === id);
      return c && c.verdict === 'ok';
    }));
    const applyIds = passes.flat();

    const esc = (v) => String(v == null ? '' : v).replace(/</g, '&lt;');
    const ok = await Swal.fire({
      //  A run that DELETES rows gets the warning icon and Cancel under the cursor.
      //  'add' only adds, so it gets the neutral question.
      icon: mode === 'replace' && t.removes > 0 ? 'warning' : 'question',
      title: mode === 'replace'
        ? `Replace the entries on ${applyIds.length} claim(s)?`
        : `Add this template to ${applyIds.length} claim(s)?`,
      html: '<div style="text-align:left;font-size:13px;line-height:1.7">'
          + `<div><b>${esc(chosen?.name || 'The template')}</b></div>`
          + (mode === 'replace' && t.removes > 0
            //  THE NUMBER THAT SHOULD STOP SOMEBODY WHO DID NOT MEAN TO. Said in its
            //  own colour and its own line, not folded into a sentence.
            ? '<div style="margin-top:8px;color:#B42318"><b>'
              + `${t.removes} existing entr${t.removes === 1 ? 'y' : 'ies'}</b> will be `
              + `REMOVED across those claims, and ${t.adds} written in their place. `
              + 'This cannot be undone.</div>'
            : `<div style="margin-top:8px">${t.adds} row(s) will be written. `
              + 'A claim that already has one keeps the one it has; nothing is doubled.</div>')
          + (t.refused
            //  Said again here, at the last screen. An officer who scrolled past the
            //  refusals above should not discover them in the result.
            ? `<div style="margin-top:8px;color:#8A5510">${t.refused} claim(s) do not suit `
              + 'this template and are being skipped — they are listed behind this dialog.</div>'
            : '')
          + (passes.length > 1
            ? `<div style="margin-top:10px"><b>${applyIds.length} claims runs in `
              + `${passes.length} passes of up to ${CHUNK}.</b> Leave this open until it `
              + 'finishes — a pass that has already run stays applied.</div>'
            : '')
          + '</div>',
      showCancelButton: true,
      confirmButtonText: `Apply to ${applyIds.length}`,
      cancelButtonText: 'Cancel',
      focusCancel: mode === 'replace' && t.removes > 0,
    });
    if (!ok.isConfirmed) return;

    const actor = (() => {
      try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || undefined; }
      catch { return undefined; }
    })();

    setBusy(true);
    setErr(null);
    let written = 0;
    let failed = 0;
    //  ── WHY A CLAIM FAILED, NOT JUST HOW MANY ─────────────────────────────────
    //  The first version reported "420 could not be saved" and nothing else. The
    //  officer had no way to tell a data problem from a bug, and the answer was
    //  sitting unread in the response: the route returns `error` per claim. Collected
    //  and counted here, because 420 copies of one sentence is one sentence.
    const reasons = new Map();
    //  A claim can be PART WRITTEN — some rows in, then a throw. That is the number
    //  that decides whether somebody has to go and look at those claims, so it is
    //  counted separately from the failure itself.
    let partial = 0;
    //  Held outside the loop so a pass that throws still reports what the earlier ones
    //  wrote. Those rows are saved.
    let stopped = null;
    try {
      for (let i = 0; i < passes.length; i += 1) {
        if (passes.length > 1) setPhase({ what: 'Applying', pass: i + 1, of: passes.length });
        try {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await axios.post(templateApply, {
            templateId: picked, claimIds: passes[i], mode, dryRun: false, actor,
          });
          written += data.written || 0;
          failed += data.failed || 0;
          for (const c of (data.claims || [])) {
            if (!c.error) continue;
            reasons.set(c.error, (reasons.get(c.error) || 0) + 1);
            if ((c.added || 0) > 0 || (c.removed || 0) > 0) partial += 1;
          }
        } catch (e) {
          stopped = e?.response?.data?.error || e.message;
          break;
        }
      }

      await Swal.fire({
        icon: (stopped || failed) ? 'warning' : 'success',
        title: stopped
          ? `Stopped after ${written} claim(s)`
          //  A run where NOTHING was written is not a success with a zero in it. It
          //  said "0 claim(s) vetted with this template" under a warning triangle and
          //  read as an odd success; it is a failure and the title should say so.
          : written === 0
            ? `Nothing was written — ${failed} claim(s) failed`
            : `${written} claim(s) vetted with this template`,
        html: '<div style="text-align:left;font-size:13px;line-height:1.7">'
            + `<div>${written} claim(s) written`
            + (failed ? `, <b>${failed} could not be saved</b>` : '')
            + (t.refused ? `, ${t.refused} skipped as unsuitable` : '')
            + '.</div>'
            //  ── THE REASON, GROUPED ────────────────────────────────────────
            //  The server says why, per claim. Without this the officer is told a
            //  number and left to guess whether it is their data or the software.
            + (reasons.size
              ? '<div style="margin-top:10px;color:#B42318"><b>Why they failed</b></div>'
                + [...reasons.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4)
                  .map(([msg, n]) => `<div style="color:#B42318">${n} claim(s): `
                    + `${esc(msg)}</div>`).join('')
                + (reasons.size > 4
                  ? `<div style="color:#8A5510">…and ${reasons.size - 4} other reason(s)</div>`
                  : '')
              : '')
            //  ── AND WHETHER ANY CLAIM WAS LEFT HALF DONE ───────────────────
            //  The one number that decides whether somebody has to go and look.
            + (partial
              ? `<div style="margin-top:10px;color:#8A5510"><b>${partial} claim(s) were `
                + 'part written</b> — some rows changed before the failure. Open those '
                + 'claims and check them.</div>'
              : '')
            + (stopped
              ? `<div style="margin-top:10px;color:#B42318"><b>It did not finish.</b> `
                + `${esc(stopped)}<br>What the earlier passes wrote is saved.</div>`
              : '')
            + '</div>',
      });

      if (onApplied) onApplied();
      //  The plan describes claims that no longer look like this. Re-asked rather than
      //  left on screen inviting a second run over the same rows.
      await checkAll();
    } finally {
      setBusy(false);
      setPhase(null);
    }
  };

  const {
    ink, inkSoft, inkMuted, inkFaint, line, greenDark: green, greenSoft,
  } = BRAND;

  return (
    <div style={{
      display: 'grid', gap: 14, flexShrink: 0, minWidth: 0,
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    }}>
      {/* ══ THE LIBRARY ══════════════════════════════════════════════════ */}
      <div style={{
        background: '#fff', border: `1px solid ${line}`, borderRadius: 12, padding: 16,
        minWidth: 0,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        }}>
          <Layers size={14} color={green} />
          <span style={{
            fontSize: 11, fontWeight: 800, letterSpacing: '.05em',
            textTransform: 'uppercase', color: inkSoft,
          }}>The template</span>
        </div>
        <div style={{ fontSize: 12, color: inkMuted, lineHeight: 1.6, marginBottom: 10 }}>
          Built under <b>Claims ▸ Condition Templates</b>. One template, applied to every
          claim it suits.
        </div>

        {/*  ── WHAT WAS TICKED ────────────────────────────────────────────────
             Said before a template is picked, because it is what decides which templates
             may be used. Read from the claims themselves — an out-patient template goes
             only on OPD claims, and an admission template only on a stay of exactly its
             length — so the officer sees the constraint rather than meeting it as a wall
             of refusals afterwards. */}
        {cohort && cohort.claims > 0 && (
          <div style={{
            border: `1px solid ${cohort.mixed ? '#F2DCB8' : `${green}33`}`,
            background: cohort.mixed ? '#FDF2E3' : greenSoft,
            borderRadius: 9, padding: '9px 11px', marginBottom: 10,
            fontSize: 11.5, lineHeight: 1.55,
            color: cohort.mixed ? '#8A5510' : inkSoft,
          }}>
            {cohort.mixed ? (
              <>
                <b>These are a mix of out-patient and admission claims.</b> No template
                suits both — an out-patient one is a single visit and an admission one is
                a stay of a set length. Go back and tick one kind.
              </>
            ) : cohort.settings[0] === 'inpatient' ? (
              <>
                <b>{cohort.claims.toLocaleString()} admission{cohort.claims === 1 ? '' : 's'}</b>
                {cohort.stayDays.length === 1
                  ? <> of <b>{cohort.stayDays[0]} day{cohort.stayDays[0] === 1 ? '' : 's'}</b>.
                      Only a {cohort.stayDays[0]}-day template can go on them.</>
                  : <> of <b>{cohort.stayDays.join(', ')} days</b>. A template matches one
                      length only, so the others will be skipped — tick one length at a
                      time to do them all.</>}
                {cohort.noDates > 0 && (
                  <span style={{ display: 'block', marginTop: 3, color: inkMuted }}>
                    {cohort.noDates} of them have no discharge date, so their length
                    cannot be checked.
                  </span>
                )}
              </>
            ) : (
              <>
                <b>{cohort.claims.toLocaleString()} out-patient
                claim{cohort.claims === 1 ? '' : 's'}</b> — a single visit, or one night of
                detention or observation. Only an out-patient template can go on them.
              </>
            )}
          </div>
        )}

        {listErr && (
          <div style={{ fontSize: 12, color: '#B42318', marginBottom: 8 }}>{listErr}</div>
        )}
        {list === null ? (
          <span style={{ fontSize: 12, color: inkFaint }}>Loading the library…</span>
        ) : list.length === 0 ? (
          <span style={{ fontSize: 12, color: inkFaint, lineHeight: 1.6 }}>
            No condition templates yet. Build one under <b>Condition Templates</b> — the
            condition, its labs and its drugs — and it will appear here.
          </span>
        ) : (
          <>
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <Search size={13} style={{
                position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
                color: inkFaint, pointerEvents: 'none',
              }} />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or condition…"
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 28px',
                  borderRadius: 8, font: 'inherit', fontSize: 12.5, background: '#fff',
                  border: `1px solid ${line}`, color: ink,
                }} />
            </div>
            <div style={{ display: 'grid', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
              {shown.length === 0 ? (
                /*  THE TWO REASONS A LIST IS EMPTY ARE DIFFERENT PROBLEMS, and telling
                    them apart is the whole value of the message. "Nothing matches" sent
                    an officer looking for a typo when the real answer was that every
                    template they own is for the other setting. */
                <span style={{ fontSize: 12, color: inkFaint, lineHeight: 1.55 }}>
                  {q.trim() && closed.length === 0
                    ? <>Nothing matches “{q.trim()}”.</>
                    : closed.length === 0
                      ? <>No templates yet. Build one under Claims ▸ Condition Templates.</>
                      : (
                        <>
                          {closed.length === 1
                            ? <>Your only template does not suit </>
                            : <>None of your {closed.length} templates suit </>}
                          {q.trim() ? <>“{q.trim()}” in </> : null}this selection
                          {cohort?.settings?.length === 1 ? (
                            cohort.settings[0] === 'inpatient'
                              ? <> — these are admissions{cohort.stayDays?.length === 1
                                ? <> of {cohort.stayDays[0]} day
                                  {cohort.stayDays[0] === 1 ? '' : 's'}</> : null}</>
                              : <> — these are out-patient claims</>
                          ) : null}.
                          {' '}Build one under Claims ▸ Condition Templates.
                        </>
                      )}
                </span>
              ) : shown.map((tpl) => {
                const on = tpl.id === picked;
                //  Why this template cannot go on THIS selection, decided before it is
                //  picked. Closed off with its reason rather than hidden: a template the
                //  officer knows they built and cannot find reads as data loss.
                const off = templateOff(tpl);
                return (
                  <button key={tpl.id} type="button"
                    disabled={!!off}
                    title={off || undefined}
                    onClick={() => { if (!off) setPicked(on ? null : tpl.id); }}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left',
                      font: 'inherit', padding: '8px 10px', borderRadius: 7, minWidth: 0,
                      cursor: off ? 'not-allowed' : 'pointer',
                      border: `1px solid ${on ? green : line}`,
                      borderLeft: `3px solid ${on ? green : 'transparent'}`,
                      background: on ? greenSoft : (off ? '#F8FAFC' : '#fff'),
                      opacity: off ? 0.72 : 1,
                    }}>
                    <span style={{
                      flexShrink: 0, width: 16, height: 16, borderRadius: 4, marginTop: 1,
                      display: 'grid', placeItems: 'center', color: '#fff',
                      border: `1px solid ${on ? green : line}`,
                      background: on ? green : '#fff',
                    }}>{on ? <Check size={10} /> : (off ? <Ban size={9} color={inkFaint} /> : null)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        display: 'block', fontSize: 12, fontWeight: on ? 800 : 700,
                        color: on ? green : (off ? inkMuted : ink), overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{tpl.name}</span>
                      <span style={{
                        display: 'block', fontSize: 10.5, color: inkFaint,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {(tpl.conditions || []).map((c) => c.name).join(' + ') || '—'}
                        {' · '}{(tpl.items || []).length} to give
                        {/*  The two facts that decide whether it may be used at all, on
                             the row — so the officer can see WHY one is closed without
                             hovering each. */}
                        {tpl.setting === 'inpatient'
                          ? ` · in-patient, ${tpl.stayDays || '?'} day${tpl.stayDays === 1 ? '' : 's'}`
                          : ' · out-patient'}
                      </span>
                      {/*  ON the row, not only in a tooltip. A greyed row whose reason
                           needs a hover is a dead end on a touch screen. */}
                      {off && (
                        <span style={{
                          display: 'block', fontSize: 10.5, color: '#8A5510',
                          marginTop: 3, lineHeight: 1.4, whiteSpace: 'normal',
                        }}>{off}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {/*  ── WHAT IS NOT ON THE LIST, AND WHY ──────────────────────────
                 Hidden by request — "I don't need all of them here" — but never
                 silently. A template the officer built and cannot find reads as data
                 loss, so the count stays visible and one click brings them back,
                 still closed off with their own reason.

                 OFFERED EVEN WHEN THE LIST IS EMPTY. That is the case where an officer
                 most needs to see what exists — nothing suits, and the question is
                 immediately "what have I got, then?". Gating this on a non-empty list
                 would answer it everywhere except there. */}
            {closed.length > 0 && (
              <button type="button" onClick={() => setShowClosed((v) => !v)}
                style={{
                  marginTop: 6, border: 'none', background: 'none', padding: 0,
                  font: 'inherit', fontSize: 11, color: inkMuted, cursor: 'pointer',
                  textAlign: 'left', lineHeight: 1.5,
                }}>
                {showClosed
                  ? <>Hide the {closed.length} that {closed.length === 1 ? 'does' : 'do'} not
                      {' '}suit these claims</>
                  : <>{closed.length} other template{closed.length === 1 ? '' : 's'}
                      {' '}{closed.length === 1 ? 'is' : 'are'} hidden — {closed.length === 1
                      ? 'it does' : 'they do'} not suit these claims.
                      {' '}<span style={{ color: green, fontWeight: 700 }}>Show</span></>}
              </button>
            )}
          </>
        )}

        {picked && (
          <div style={{ marginTop: 12 }}>
            <div style={{
              fontSize: 11, fontWeight: 800, letterSpacing: '.05em',
              textTransform: 'uppercase', color: inkSoft, marginBottom: 6,
            }}>How</div>
            <div style={{ display: 'grid', gap: 5 }}>
              {MODES.map((m) => {
                const on = m.key === mode;
                return (
                  <button key={m.key} type="button" onClick={() => setMode(m.key)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left',
                      font: 'inherit', padding: '8px 10px', borderRadius: 7, minWidth: 0,
                      cursor: 'pointer',
                      border: `1px solid ${on ? green : line}`,
                      background: on ? greenSoft : '#fff',
                    }}>
                    <span style={{
                      flexShrink: 0, width: 13, height: 13, borderRadius: 999, marginTop: 2,
                      border: `4px solid ${on ? green : line}`, background: '#fff',
                    }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{
                        display: 'block', fontSize: 12, fontWeight: on ? 800 : 700,
                        color: on ? green : inkSoft,
                      }}>{m.label}</span>
                      <span style={{
                        display: 'block', fontSize: 11, color: inkMuted, marginTop: 1,
                        lineHeight: 1.45,
                      }}>{m.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ══ WHAT IT WOULD DO TO THIS SELECTION ═══════════════════════════ */}
      <div style={{
        background: '#fff',
        border: `1px solid ${ready ? `${green}55` : line}`,
        borderRadius: 12, padding: 16, minWidth: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Users size={14} color={green} />
          <span style={{
            fontSize: 11, fontWeight: 800, letterSpacing: '.05em',
            textTransform: 'uppercase', color: inkSoft,
          }}>
            What it would do · {ids.length.toLocaleString()} claim(s)
          </span>
        </div>

        {!picked ? (
          <div style={{ fontSize: 12, color: inkFaint, lineHeight: 1.6 }}>
            Pick a template on the left. Every ticked claim is then checked against its
            own patient — the date of birth, the gender and whether it is an admission —
            and the ones it does not suit are named here.
          </div>
        ) : busy && !plan ? (
          <div style={{
            fontSize: 12, color: inkMuted, display: 'flex', alignItems: 'center', gap: 7,
          }}>
            <Loader2 size={13} className="mf-spin" />
            {phase ? `${phase.what} — pass ${phase.pass} of ${phase.of}…` : 'Checking…'}
          </div>
        ) : err ? (
          <div style={{ fontSize: 12, color: '#B42318', lineHeight: 1.6 }}>{err}</div>
        ) : plan ? (
          <>
            {/*  THE THREE NUMBERS, before any detail. This is the answer to "can I run
                 this", and it is one line. */}
            <div style={{
              display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
              marginBottom: 10,
            }}>
              {[
                ['Can take it', t.ok, green],
                ['Cannot', t.refused, t.refused ? '#8A5510' : inkFaint],
                ['Not found', t.missing, t.missing ? '#B42318' : inkFaint],
              ].map(([label, n, colour]) => (
                <div key={label} style={{
                  border: `1px solid ${line}`, borderRadius: 8, padding: '7px 9px',
                }}>
                  <div style={{
                    fontSize: 17, fontWeight: 800, color: colour, lineHeight: 1.1,
                    fontVariantNumeric: 'tabular-nums',
                  }}>{n.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: inkFaint, fontWeight: 700 }}>{label}</div>
                </div>
              ))}
            </div>

            {/*  WHAT WOULD BE WRITTEN, across the claims that can take it. */}
            {t.ok > 0 && (
              <div style={{ fontSize: 12, lineHeight: 1.65, marginBottom: 10 }}>
                {mode === 'replace' && t.removes > 0 && (
                  <div style={{ color: '#B42318', display: 'flex', gap: 6 }}>
                    <Trash2 size={12} style={{ flexShrink: 0, marginTop: 3 }} />
                    <span>
                      <b>{t.removes.toLocaleString()}</b> existing
                      {' '}entr{t.removes === 1 ? 'y' : 'ies'} removed
                    </span>
                  </div>
                )}
                <div style={{ color: green, display: 'flex', gap: 6 }}>
                  <Plus size={12} style={{ flexShrink: 0, marginTop: 3 }} />
                  <span><b>{t.adds.toLocaleString()}</b> row(s) written</span>
                </div>
                {/*  ── AND WHAT THEY COME OUT BILLED ON ────────────────────────
                     The out-patient service is the principal G-DRG and it lives on a
                     diagnosis row, so replacing the diagnoses can carry it off and move
                     the specialty with it. Stated here, before the run. */}
                {billed.carried > 0 && (
                  <div style={{ color: inkMuted, display: 'flex', gap: 6 }}>
                    <Stethoscope size={12} style={{ flexShrink: 0, marginTop: 3 }} />
                    <span>
                      <b>{billed.carried.toLocaleString()}</b> keep the service they are
                      {' '}already billed on — this template names none, so it is carried
                      {' '}onto the new diagnosis rather than lost to them
                    </span>
                  </div>
                )}
                {billed.changed > 0 && (
                  <div style={{ color: '#8A5510', display: 'flex', gap: 6 }}>
                    <Stethoscope size={12} style={{ flexShrink: 0, marginTop: 3 }} />
                    <span>
                      <b>{billed.changed.toLocaleString()}</b> change what they are billed
                      {' '}on, because this template names its own service
                    </span>
                  </div>
                )}
                {billed.noService > 0 && (
                  <div style={{ color: '#B42318', display: 'flex', gap: 6 }}>
                    <Stethoscope size={12} style={{ flexShrink: 0, marginTop: 3 }} />
                    <span>
                      <b>{billed.noService.toLocaleString()}</b> out-patient claim(s) have
                      {' '}no OPD service code and this template names none, so they stay
                      {' '}billed on a diagnosis code. Set a service on the template.
                    </span>
                  </div>
                )}
              </div>
            )}

            {/*  ── WHY THE REST CANNOT ────────────────────────────────────────
                 Grouped by reason with the patients named under it. Eleven claims
                 refused for one reason is one sentence and a count; eleven sentences
                 is a wall the officer scrolls past. */}
            {refusalGroups.length > 0 && (
              <div style={{
                border: '1px solid #F2DCB8', background: '#FDF2E3', borderRadius: 9,
                padding: '9px 11px', display: 'grid', gap: 8, maxHeight: 260,
                overflowY: 'auto',
              }}>
                {refusalGroups.map((g, i) => (
                  <div key={i} style={{ fontSize: 11.5, color: '#8A5510', lineHeight: 1.5 }}>
                    <div style={{ display: 'flex', gap: 6, fontWeight: 700 }}>
                      <Ban size={12} style={{ flexShrink: 0, marginTop: 3 }} />
                      <span>
                        {g.who.length.toLocaleString()} claim(s)
                        {g.reason === 'age_range' ? ' — outside this template’s age range' : ''}
                        {g.kind === 'age' && g.reason !== 'age_range' ? ' — wrong age band' : ''}
                        {g.kind === 'sex' ? ' — wrong gender' : ''}
                        {g.kind === 'setting' ? ' — in-patient / out-patient' : ''}
                        {g.name || g.code ? `: ${g.name || g.code}` : ''}
                      </span>
                    </div>
                    {/*  ── THE SENTENCE, ONLY WHEN IT IS TRUE OF THE WHOLE GROUP ──
                         The server's `why` names A PATIENT — "AMA ATAA is an adult of
                         82" — because it is written for one claim at a time. Shown above
                         a list of two names it reads as though it described both, and
                         the second patient is 21, not 82.

                         So the full sentence stands only for a group of one. For a bigger
                         group the server also sends `whyAll`: the same rule stated without
                         the patient in it. It is NOT re-derived here. This panel used to
                         build that sentence itself from the kind, and got it wrong — every
                         age refusal was described as a tariff in the wrong band, so 17
                         claims refused for being outside a 19–45 template were reported as
                         children, and there was nothing wrong with their dates of birth. */}
                    <div style={{ paddingLeft: 18, color: '#8A5510', opacity: 0.95 }}>
                      {g.who.length === 1
                        ? (g.who[0].refusals || []).find(
                          (r) => r.kind === g.kind
                            && (r.reason || '') === (g.reason || '')
                            && (r.code || '') === (g.code || '')
                        )?.why
                        //  No whyAll means an older backend. Falling back to the first
                        //  patient's own sentence is imprecise about the others but it is
                        //  TRUE of the rule that fired, which the invented sentence was
                        //  not.
                        : g.whyAll || (g.who[0].refusals || []).find(
                          (r) => r.kind === g.kind
                            && (r.reason || '') === (g.reason || '')
                            && (r.code || '') === (g.code || '')
                        )?.why}
                    </div>
                    <div style={{ paddingLeft: 18, fontSize: 10.5, color: inkMuted }}>
                      {g.who.slice(0, 6).map((c) => c.facts?.name || c.id).join(', ')}
                      {g.who.length > 6 ? ` and ${g.who.length - 6} more` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {t.ok === 0 && t.refused > 0 && (
              <div style={{
                marginTop: 10, fontSize: 11.5, color: '#8A5510', lineHeight: 1.6,
                fontWeight: 700,
              }}>
                None of these claims can take this template. Pick the one for the other
                age band, or narrow the pattern on the left.
              </div>
            )}

            {/* ── THE APPLY ────────────────────────────────────────────── */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap',
              borderTop: `1px solid ${line}`, marginTop: 12, paddingTop: 11,
            }}>
              <button type="button" onClick={apply} disabled={!ready}
                //  The REASON it is dead, on the control itself. A greyed Apply with no
                //  explanation is where an officer stops and asks somebody.
                title={!picked ? 'Pick a template first'
                  : busy ? 'Checking…'
                    : t.ok === 0 ? 'No ticked claim can take this template'
                      : undefined}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, font: 'inherit',
                  padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
                  border: '1px solid transparent', color: '#fff',
                  cursor: ready ? 'pointer' : 'not-allowed',
                  background: ready ? green : inkFaint,
                }}>
                {busy ? <Loader2 size={13} className="mf-spin" /> : <Check size={13} />}
                {busy && phase
                  ? `${phase.what} ${phase.pass}/${phase.of}…`
                  : `Apply to ${(t.ok || 0).toLocaleString()}`}
              </button>
              {/*  Beside the button, not in a tooltip on it: this is the sentence that
                   stops somebody pressing it by mistake. */}
              {ready && mode === 'replace' && t.removes > 0 && (
                <span style={{
                  fontSize: 11, color: '#8A5510', fontWeight: 700, lineHeight: 1.5,
                }}>
                  <AlertTriangle size={11} style={{ verticalAlign: -1 }} />
                  {' '}This removes {t.removes.toLocaleString()} existing
                  {' '}entr{t.removes === 1 ? 'y' : 'ies'}. It cannot be undone.
                </span>
              )}
              {ready && t.refused > 0 && (
                <span style={{ fontSize: 11, color: inkMuted }}>
                  {t.refused.toLocaleString()} skipped
                </span>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
